import { FC, Ref, useRef } from "react";

import { SidebarOptionsMenu, SidebarOptionsMenuProps } from "./SidebarOptionsMenu";

import {
  showAddSectionModal,
  showCopySectionModal,
  showMoveSectionModal,
} from "components/materials/home/admin/content/helpers";
import { PublicationStatus, SectionType } from "components/server-types";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { MenuInstance, MenuItem } from "mds/components/Menu";
import {
  ArrowDownToLineIcon,
  BookSectionLinkedIcon,
  BookSectionTemplateIcon,
  CircleCrossIcon,
  CopySendArrowIcon,
  EyeNotVisibleIcon,
  EyeVisibleIcon,
  GearSettingsIcon,
  PencilEditIcon,
} from "mds/icons";
import { useAppSelector } from "store/index";
import { selectLatestPublicationForSectionId } from "store/selectors/publications";
import { useIsArchived } from "utils/hooks/use-is-archived";
import { isLinkedSection, isTemplateSection, isUnlinkedSection } from "utils/publishing";

interface SectionSidebarOptionsMenuProps extends SidebarOptionsMenuProps {
  section: SectionType;
  optionsMenuActions: SidebarOptionsMenuProps["optionsMenuActions"] & {
    onSettings?: () => void;
    onRename?: () => void;
    onCopyTo?: (sectionId: string, courseId?: string, termId?: string) => void;
    onMoveTo?: (courseId: string, termId: string) => void;
    onExport?: () => void;
    onArchive?: () => void;
    onUnarchive?: () => void;
    onDuplicateAsTemplate?: () => void;
    onUnlinkSection?: () => void;
  };
}

export const SectionSidebarOptionsMenu: FC<SectionSidebarOptionsMenuProps> = ({
  styles,
  className,
  direction = "bottom",
  section,
  optionsMenuActions,
}) => {
  const sectionSidebarMenuRef: Ref<MenuInstance> = useRef(null);
  const { canUnarchive, isArchived } = useIsArchived();
  const isSectionArchived = isArchived(section);
  const unarchiveDisabled = !canUnarchive(section);
  const templatePublication = useAppSelector((state) =>
    selectLatestPublicationForSectionId(state, section.template_id),
  );
  const isTemplatePublishing = templatePublication?.status === PublicationStatus.STARTED;

  const copyToMenuItem = optionsMenuActions.onCopyTo && !isLinkedSection(section) && (
    <MenuItem
      onClick={() => showCopySectionModal({ section, onCopyTo: optionsMenuActions.onCopyTo! })}
    >
      <IconText iconStart={<CopySendArrowIcon />} text={t("sidebar_options_menu.copy_to")} />
    </MenuItem>
  );

  const moveToMenuItem = optionsMenuActions.onMoveTo && isUnlinkedSection(section) && (
    <MenuItem
      onClick={() => showMoveSectionModal({ section, onMoveTo: optionsMenuActions.onMoveTo! })}
    >
      <IconText iconStart={<CopySendArrowIcon />} text={t("sidebar_options_menu.move_section")} />
    </MenuItem>
  );

  const sectionSettingItem = optionsMenuActions.onSettings && (
    <MenuItem
      eventName="Admin Courses - Section Settings - Open"
      title={t("settings.section_settings")}
      onClick={optionsMenuActions.onSettings}
    >
      <IconText iconStart={<GearSettingsIcon />} text={t("settings.section_settings")} />
    </MenuItem>
  );

  const addLinkedSectionMenuItem = isTemplateSection(section) && !isSectionArchived && (
    <MenuItem
      onClick={() =>
        showAddSectionModal({
          defaultCourseId: section.course_id,
          defaultTermId: section.term_id,
          defaultTemplateId: section.id,
        })
      }
    >
      <IconText
        iconStart={<BookSectionLinkedIcon />}
        text={t("sidebar_options_menu.add_linked_section")}
      />
    </MenuItem>
  );

  const renameMenuItem = optionsMenuActions.onRename && (
    <MenuItem onClick={optionsMenuActions.onRename}>
      <IconText iconStart={<PencilEditIcon />} text={t("sidebar_options_menu.rename_section")} />
    </MenuItem>
  );

  const exportMenuItem = optionsMenuActions.onExport && (
    <MenuItem onClick={optionsMenuActions.onExport}>
      <IconText iconStart={<ArrowDownToLineIcon />} text={t("common.export")} />
    </MenuItem>
  );

  const archiveMenuItem = !isSectionArchived && optionsMenuActions.onArchive && (
    <MenuItem onClick={optionsMenuActions.onArchive}>
      <IconText iconStart={<EyeNotVisibleIcon />} text={t("sidebar_options_menu.archive")} />
    </MenuItem>
  );

  const unarchiveMenuItem = isSectionArchived && optionsMenuActions.onUnarchive && (
    <MenuItem disabled={unarchiveDisabled} onClick={optionsMenuActions.onUnarchive}>
      <IconText iconStart={<EyeVisibleIcon />} text={t("sidebar_options_menu.unarchive")} />
    </MenuItem>
  );

  const duplicateAsTemplateMenuItem = optionsMenuActions.onDuplicateAsTemplate &&
    isUnlinkedSection(section) && (
      <MenuItem onClick={optionsMenuActions.onDuplicateAsTemplate}>
        <IconText
          iconStart={<BookSectionTemplateIcon />}
          text={t("sidebar_options_menu.duplicate_as_template")}
        />
      </MenuItem>
    );

  const unlinkSectionMenuItem = optionsMenuActions.onUnlinkSection && isLinkedSection(section) && (
    <MenuItem disabled={isTemplatePublishing} onClick={optionsMenuActions.onUnlinkSection}>
      <IconText
        iconStart={<CircleCrossIcon />}
        text={t("sidebar_options_menu.unlink_from_template")}
      />
    </MenuItem>
  );

  return (
    <SidebarOptionsMenu
      className={className}
      direction={direction}
      instanceRef={sectionSidebarMenuRef}
      optionsMenuActions={optionsMenuActions}
      styles={styles}
    >
      {sectionSettingItem}
      {addLinkedSectionMenuItem}
      {renameMenuItem}
      {copyToMenuItem}
      {moveToMenuItem}
      {exportMenuItem}
      {archiveMenuItem}
      {unarchiveMenuItem}
      {duplicateAsTemplateMenuItem}
      {unlinkSectionMenuItem}
    </SidebarOptionsMenu>
  );
};
